/**
 * WHAT A BEAST GOD IS TOLD — the Beast read model (`sim/wildlife/reading.ts`),
 * said in the world's voice.
 *
 * The counterpart of `ui/thread.ts` for the wild: `reading.ts` computes typed
 * readings off the ledger and this turns each into the one or two sentences a
 * tracker would actually say. `main.ts`'s card decides WHICH of them a god is
 * shown, off the Godhood Web's rules — this file does not know the Web exists
 * and takes each reading on its own.
 *
 * Every rule `ui/lives.ts` and `ui/subject.ts` keep, this keeps, and for the
 * same reasons:
 *
 * **No number that has to be compared against another number.** A herd is
 * *thinner on the ground than the herds around them*, never `0.63×`. The
 * moment the card prints a ratio the player has stopped watching animals and
 * started auditing a table.
 *
 * **The world states, it does not explain — and it does not invent.** Every
 * sentence below is licensed by a field the read model computed off the
 * ledger, the migration predicate, the K decomposition, the events ring or
 * what the caller said about people. Where a reading found nothing true to
 * say, this says nothing: an honest silence beats a plausible sentence.
 *
 * **Nothing names a hand.** A pressed herd reads as *something is leaning on
 * them that the ground does not explain*, whoever's hand it was (law 10).
 *
 * DOM-free, three-free, sim-write-free. `test/beasts.ts` runs it against a
 * live world with nothing rendered.
 */

import { DAY, YEAR_DAYS } from '@/sim/clock';
import type {
  BeastReading, BranchReading, CarrionReading, CompanyReading, CompareReading,
  DriveReading, MortalReading, MovementReading, PopulationReading, PreyReading,
  RangeReading, RoleReading, SeasonReading,
} from '@/sim/wildlife/reading';
import type { WildlifeSpeciesId } from '@/sim/wildlife/types';

/** The kinds, as a tracker names them in the plural. */
export const KIND: Readonly<Record<string, string>> = {
  grazer: 'grazers', browser: 'browsers', small_game: 'small game',
  predator: 'hunters', scavenger: 'scavengers', fish: 'fish',
};
const kind = (s: string): string => KIND[s] ?? 'animals';

/** World-time since, said as a tracker would. */
const since = (at: number, now: number): string => {
  const days = Math.max(0, (now - at) / DAY);
  if (days < 45) return 'within the month';
  if (days < YEAR_DAYS) return 'within the year';
  const y = Math.floor(days / YEAR_DAYS);
  return y === 1 ? 'a year and more' : y < 12 ? `${y} years` : 'longer than anyone here remembers';
};

/* ── the awakening: what they are doing about their ground ─────────────── */

/**
 * The dominant current state, and the one hand-free line about pressure.
 * Says the range wear too, because at the intimate register this REPLACES
 * `ui/thread.ts`'s `herdTrend` and must not lose what that already said.
 */
export function driveNow(d: DriveReading, species: WildlifeSpeciesId, now: number, range: number): string[] {
  const out: string[] = [];
  switch (d.drive) {
    case 'gathering': out.push('gathering onto this ground — fewer of them than it could carry'); break;
    case 'holding': out.push('holding to this ground, about as many as it carries'); break;
    case 'thinning': out.push('more of them than this ground can feed — they are dying back on it'); break;
    case 'leaving':
      out.push(d.inclination.declined
        ? 'leaving ground that has failed them'
        : 'walking off this ground — there is better ground beside it');
      break;
    case 'hunting': out.push('hunting this ground, and it feeds them'); break;
    case 'starving': out.push('hungrier than this ground can feed'); break;
    case 'absent':
      out.push(d.absentSince !== undefined
        ? `none of the ${kind(species)} stand here now — gone ${since(d.absentSince, now)}`
        : `none of the ${kind(species)} stand here now`);
      return out;
  }
  if (range < 0.55) out.push('they have grazed this ground bare');
  else if (range < 0.8) out.push('the ground under them is wearing thin');
  if (d.pressed > 0.05) out.push('and something is leaning on them that the ground does not explain');
  else if (d.pressed < -0.05) out.push('and something is holding them to this ground that the ground does not explain');
  return out;
}

/* ── inclination ───────────────────────────────────────────────────────── */

export function companyNow(c: CompanyReading, species: WildlifeSpeciesId): string[] {
  if (species === 'predator') {
    const who = c.head < 1.5 ? 'a lone hunter on this ground'
      : c.head < 2.5 ? 'a pair of hunters on this ground' : 'several hunters on this ground';
    return [who, c.neighboursHolding === 0 ? 'and none of their kind on the ground around it'
      : 'and more of their kind on the ground around it'];
  }
  if (c.neighbours === 0) return [];
  if (c.neighboursHolding === 0) return ['the only ones of their kind on this ground or the next'];
  if (c.neighboursHolding === c.neighbours) return ['their kind holds every ground around this one'];
  return [c.neighboursHolding >= c.neighbours / 2
    ? 'more of their kind stand on most of the ground around'
    : 'a few more of their kind stand on the ground around'];
}

export function rangeNow(r: RangeReading, species: WildlifeSpeciesId): string[] {
  const out: string[] = [];
  // one line for where this ground sits in their country, and — with the
  // wider survey Known Range's later ranks buy — how much of that country
  // their kind holds; said together, because said apart it was three lines
  // agreeing with one another
  const wide = r.reach >= 2 && r.surveyed > 0
    ? (r.holding / r.surveyed >= 0.6 ? 'most' : r.holding / r.surveyed >= 0.25 ? 'some' : 'little')
    : null;
  if (r.standing === 'middle') {
    out.push(wide ? `the middle of their country — their kind holds ${wide} of the ground round about`
      : 'the middle of their country');
  } else if (r.standing === 'edge') {
    out.push(wide ? `the edge of their country — their kind holds ${wide} of the ground beyond`
      : 'the edge of their country — fewer of their kind stand beyond');
  } else {
    out.push(`further out than ${kind(species)} stand anywhere near`);
  }
  if (r.latelyLost > 0) {
    out.push(r.latelyLost === 1 ? 'ground they were on within the year lies empty beside this'
      : 'ground they were on within the year lies empty around here');
  }
  return out;
}

export function movementNow(m: MovementReading, depth: number): string[] {
  const out: string[] = [];
  if (m.arrived > 0 && m.arrivedFrom) out.push(`some of them came onto this ground from the ${m.arrivedFrom} lately`);
  if (m.left > 0 && m.leftFor) out.push(`some of them walked off ${m.leftFor} lately`);
  if (depth >= 2 && m.left > 0) {
    out.push(m.fled ? 'they fled ground that had failed them' : 'they drifted after better ground');
  }
  return out;
}

export function compareNow(c: CompareReading): string[] {
  if (c.against === 0) return ['no others of their kind near enough to set them against'];
  const out: string[] = [];
  if (c.density >= 1.5) out.push('denser on the ground than the herds around them');
  else if (c.density <= 0.67) out.push('thinner on the ground than the herds around them');
  else out.push('about as many as the herds around them');
  if (c.trend === 'better') out.push('and doing better than their neighbours');
  else if (c.trend === 'worse') out.push('and doing worse than the herds around them');
  if (c.wear === 'worse') out.push('on ground more worn than their neighbours\'');
  else if (c.wear === 'better') out.push('on ground less worn than their neighbours\'');
  return out;
}

export function seasonNow(s: SeasonReading | null): string[] {
  if (!s) return [];
  if (s.curing) return ['the grass here is curing in the dry'];
  if (s.refuge) return ['this is ground by the water — it stays green when the rest cures'];
  return [];
}

export function carrionNow(c: CarrionReading): string[] {
  if (c.dying) {
    return [c.gathering
      ? 'something is dying on this ground, and the scavengers are gathering to it'
      : 'something is dying on this ground'];
  }
  if (c.gathering) return ['the scavengers are gathering here'];
  return [];
}

/* ── practice ──────────────────────────────────────────────────────────── */

export function preyNow(p: PreyReading, species: WildlifeSpeciesId, depth: number): string[] {
  const out: string[] = [];
  if (species === 'predator' || species === 'scavenger') {
    if (species === 'predator') {
      if (p.hungry) out.push('hungrier than this ground can feed — the prey here has thinned');
      else if (p.mainPrey) out.push(`the ground feeds them, ${kind(p.mainPrey)} mostly`);
    }
    return out;
  }
  // prey: what hunts them and how hard — rank II
  if (depth < 2) return out;
  if (p.hunted === 'hard') out.push('hard hunted by what hunts this ground');
  else if (p.hunted === 'some') out.push('what hunts this ground takes some of them');
  else if (p.hunted === 'little') out.push('what hunts this ground takes little of them');
  return out;
}

export function mortalNow(m: MortalReading, species: WildlifeSpeciesId, depth: number): string[] {
  const out: string[] = [];
  if ((m.huntersHere ?? 0) > 0) out.push('people are hunting this ground');
  else if ((m.huntersNear ?? 0) > 0) out.push('people are hunting the next ground');
  if (depth >= 2 && species === 'predator' && m.incidents > 0) {
    out.push('people met these hunters here lately');
  }
  return out;
}

/** What Strengthen would take hold of, on a herd — the process it is in. */
export function reinforceableBeast(
  d: DriveReading, species: WildlifeSpeciesId,
  has: { movement: boolean; increase: boolean; hunt: boolean },
): string[] {
  const out: string[] = [];
  const isHunter = species === 'predator';
  if (has.movement && !isHunter && d.head >= 2 && d.inclination.gradient > 0)
    out.push('their drift off this ground');
  if (has.increase && !isHunter && d.drive === 'gathering') out.push('their increase');
  if (has.hunt && isHunter && (d.drive === 'hunting' || d.drive === 'starving') && d.head >= 1)
    out.push('their hunting');
  return out;
}

/* ── mastery ───────────────────────────────────────────────────────────── */

export function branchNow(b: BranchReading): string[] {
  const out: string[] = [];
  if (b.nearLeaving) out.push('close to leaving this ground, and not yet decided');
  if (b.groundFailing) out.push('the ground is failing under them');
  if (b.overstretched) out.push('more of them than worn ground can hold — this will not last');
  return out;
}

/**
 * THE LOCAL POPULATION — the larger subject a herd resolves into past its
 * own footprint. Sized in the words a tracker uses for country, never cells.
 */
export function populationNow(p: PopulationReading, opts: { hunters?: boolean } = {}): string[] {
  const out: string[] = [];
  const n = p.cells.length;
  if (n === 0) return [`no ${kind(p.species)} hold this ground`];
  out.push(n <= 2 ? `the ${kind(p.species)} of this ground and the next`
    : n <= 6 ? `the ${kind(p.species)} of this valley`
    : `the ${kind(p.species)} of this country`);
  if (p.growing > p.declining && p.growing >= Math.ceil(n / 3)) out.push('filling their ground');
  else if (p.declining > p.growing && p.declining >= Math.ceil(n / 3)) out.push('emptying it');
  else out.push('holding their ground');
  if (p.leaving > 0) out.push(p.leaving >= n / 2 ? 'much of them on the move' : 'part of them on the move');
  if (p.worn > 0) out.push(p.worn >= n / 2 ? 'the ground worn under most of them' : 'the ground worn in places');
  if (opts.hunters !== false && p.hunted !== undefined && p.hunted > 0)
    out.push(p.hunted >= n / 2 ? 'people hunt most of it' : 'people hunt part of it');
  if (p.sharedWithHunters > 0)
    out.push(p.sharedWithHunters >= n / 2 ? 'they share most of it with hunters' : 'hunters stand on part of it');
  if (p.latelyLost > 0) out.push('they have lost ground within the year');
  if (p.returned > 0) out.push('and come back onto ground they were gone from');
  if (p.truncated) out.push('and the country goes on beyond what can be seen from here');
  return out;
}

/** The register word for the population subject — `CardView.at`. */
export const populationWord = (species: WildlifeSpeciesId): string =>
  `the ${kind(species)} of this country`;

/* ── transcendence ─────────────────────────────────────────────────────── */

export function roleNow(r: RoleReading | null, species: WildlifeSpeciesId, depth: number): string[] {
  if (!r) return [];
  const out: string[] = [];
  const t = r.terms;
  const grazer = species === 'grazer' || species === 'small_game';
  const browser = species === 'browser' || species === 'small_game';
  if (species === 'predator') {
    if (r.mainPrey) out.push(`the hunters here live on the ${kind(r.mainPrey)}`);
    return out;
  }
  if (species === 'scavenger') {
    out.push(r.carrionCarried ? 'carried by what dies on this ground' : 'drawn by the unrest of this ground');
    return out;
  }
  if (r.stage === 'grass') out.push(grazer ? 'the burn\'s grass years are carrying them' : 'a burn\'s grass years — little browse for them yet');
  else if (r.stage === 'shrub') out.push(browser ? 'scrub grown back after a fire feeds them here' : 'scrub where the grass was — poorer grazing');
  else if (r.stage === 'young') out.push(browser ? 'young wood grown back since a fire, and browse in it' : 'young wood shading out their grazing');
  else if (r.stage === 'bare') out.push('burnt ground that has not come back — little here for them');
  if (t.cure >= 0.12) out.push('the season has cured the grass');
  if (t.rangeFactor < 0.8) out.push('the range is worn under them, and carries less');
  if (depth >= 2 && (grazer || browser) && r.huntersHere >= 1) {
    // the chain, one link on: what they carry in turn
    out.push('and what hunts this ground lives on them');
  }
  return out;
}

/* ── the whole card, gated ─────────────────────────────────────────────── */

/** Which readings a god may be shown — the Web's rules, resolved by the caller. */
export interface BeastSight {
  drive: boolean;
  company: boolean;
  range: boolean;
  movement: number;
  compare: boolean;
  season: boolean;
  carrion: boolean;
  prey: number;
  mortal: number;
  branch: boolean;
  role: number;
}

/** No Beast investment at all — the base card says what it always did. */
export const NO_SIGHT: BeastSight = {
  drive: false, company: false, range: false, movement: 0, compare: false, season: false,
  carrion: false, prey: 0, mortal: 0, branch: false, role: 0,
};

/**
 * The herd's lines, in the order a card reads them: what they are doing,
 * whom they are with, where their kind stands, what is pressing on them, what
 * lies behind it — and only the readings the god has earned. Order is fixed
 * because it is the order the questions outrank each other.
 */
export function beastNow(r: BeastReading, sight: BeastSight, now: number): string[] {
  const out: string[] = [];
  const range = r.role?.terms.range ?? 1;
  if (sight.drive) out.push(...driveNow(r.drive, r.species, now, range));
  if (r.drive.drive === 'absent') return out;
  // Living Company's "their kind holds every ground around this one" IS Known
  // Range's "the middle of their country" — one fact, and it is said once
  if (sight.company && !(sight.range && r.range.standing === 'middle' && r.species !== 'predator')) {
    out.push(...companyNow(r.company, r.species));
  }
  if (sight.range) out.push(...rangeNow(r.range, r.species));
  if (sight.movement > 0) out.push(...movementNow(r.movement, sight.movement));
  if (sight.branch) out.push(...branchNow(r.branch));
  if (sight.prey > 0) out.push(...preyNow(r.prey, r.species, sight.prey));
  if (sight.mortal > 0) out.push(...mortalNow(r.mortal, r.species, sight.mortal));
  if (sight.season) out.push(...seasonNow(r.season));
  if (sight.carrion) out.push(...carrionNow(r.carrion));
  if (sight.compare) out.push(...compareNow(r.compare));
  if (sight.role > 0) out.push(...roleNow(r.role, r.species, sight.role));
  return out;
}
